import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Ban, Save } from "lucide-react";
import { api } from "../../lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const AvailabilityManager = () => {
  const [allSlots, setAllSlots] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [blockDate, setBlockDate] = useState("");
  const [blockSlot, setBlockSlot] = useState("");

  const load = async () => {
    try {
      const [s, b] = await Promise.all([api.get("/availability/schedule"), api.get("/availability/blocked")]);
      setSchedule(s.data.schedule); setAllSlots(s.data.all_slots); setBlocks(b.data);
    } catch { toast.error("Failed to load availability"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (day, slot) => {
    setSchedule((sc) => {
      const cur = sc[day] || [];
      return { ...sc, [day]: cur.includes(slot) ? cur.filter((s) => s !== slot) : [...cur, slot] };
    });
  };
  const toggleDay = (day) => setSchedule((sc) => ({ ...sc, [day]: (sc[day] || []).length ? [] : [...allSlots] }));

  const saveSchedule = async () => {
    setSaving(true);
    try { await api.put("/availability/schedule", { schedule }); toast.success("Weekly hours saved"); }
    catch { toast.error("Failed to save schedule"); }
    finally { setSaving(false); }
  };

  const addBlock = async () => {
    if (!blockDate) return toast.error("Pick a date to block");
    try {
      await api.post("/availability/blocked", { date: blockDate, time_slot: blockSlot || null });
      toast.success(blockSlot ? "Slot blocked" : "Full day blocked");
      setBlockDate(""); setBlockSlot(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to block"); }
  };

  const removeBlock = async (id) => {
    try { await api.delete(`/availability/blocked/${id}`); toast.success("Unblocked"); load(); }
    catch { toast.error("Failed to remove"); }
  };

  if (!schedule) return <div className="py-20 grid place-items-center text-[#55703F]"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="mt-6 grid gap-6" data-testid="availability-manager">
      <div className="rounded-2xl bg-white border border-[#C9DFAC] p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-serif text-xl text-[#20301A]">Weekly working hours</h2>
            <p className="text-xs text-[#55703F] mt-1">Tap slots to open or close them. Clients only see open slots that aren't booked or blocked.</p>
          </div>
          <button data-testid="save-schedule-btn" onClick={saveSchedule} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-5 py-2.5 text-xs hover:bg-[#20301A] disabled:opacity-60">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save hours
          </button>
        </div>
        <div className="mt-5 grid gap-3">
          {DAYS.map((name, d) => (
            <div key={name} className="flex flex-col sm:flex-row sm:items-center gap-2 border-t border-[#DFF0C4] pt-3">
              <button data-testid={`day-toggle-${d}`} onClick={() => toggleDay(String(d))}
                className={`w-28 shrink-0 text-left text-sm font-medium ${(schedule[String(d)] || []).length ? "text-[#20301A]" : "text-[#55703F]/50 line-through"}`}>
                {name}
              </button>
              <div className="flex flex-wrap gap-1.5">
                {allSlots.map((slot) => {
                  const on = (schedule[String(d)] || []).includes(slot);
                  return (
                    <button key={slot} data-testid={`slot-${d}-${slot.replace(/[: ]/g, "")}`} onClick={() => toggle(String(d), slot)}
                      className={`rounded-full px-3 py-1.5 text-[0.7rem] border transition-all ${on ? "bg-[#DFF0C4] border-[#3A7D32] text-[#20301A]" : "border-[#C9DFAC] text-[#55703F] hover:border-[#3A7D32]"}`}>
                      {slot}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#C9DFAC] p-6">
        <h2 className="font-serif text-xl text-[#20301A]">Block specific dates & slots</h2>
        <p className="text-xs text-[#55703F] mt-1">For holidays, breaks or personal time. Leave the slot empty to block the whole day.</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input data-testid="block-date-input" type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)}
            className="rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-2.5 text-sm outline-none focus:border-[#3A7D32]" />
          <select data-testid="block-slot-select" value={blockSlot} onChange={(e) => setBlockSlot(e.target.value)}
            className="rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-2.5 text-sm outline-none focus:border-[#3A7D32]">
            <option value="">Whole day</option>
            {allSlots.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="add-block-btn" onClick={addBlock}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#FFC9A0] bg-[#FFEFDF] text-[#B0500F] px-5 py-2.5 text-xs hover:bg-[#FFE3C8]">
            <Ban size={13} /> Block
          </button>
        </div>
        {blocks.length === 0 ? (
          <p className="mt-4 text-sm text-[#55703F]" data-testid="no-blocks-msg">No blocked dates yet.</p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="blocks-list">
            {blocks.map((b) => (
              <span key={b.id} data-testid={`block-item-${b.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-[#FFE3C8] border border-[#FFC9A0] px-4 py-2 text-xs text-[#B0500F]">
                {b.date} · {b.time_slot || "Whole day"}
                <button data-testid={`remove-block-${b.id}`} onClick={() => removeBlock(b.id)} className="hover:text-[#20301A]"><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
